/**
 * Curated short suggestions shown next to the greeting in the header.
 * Picked once per sign-in (cached in localStorage keyed by user id) so
 * the prompt is stable across reloads but feels fresh on each new
 * session.
 *
 * Each suggestion carries the destination it should take the user to so
 * the prompt actually does what it says (it previously always linked to
 * the profile page). `event`, when present, is a window CustomEvent name
 * dispatched after navigation — used to open the AI chat widget or focus
 * the post composer, which aren't routes of their own.
 *
 * Add freely. Keep each under ~50 chars so they fit on one line on
 * tablet-width viewports.
 */
export interface GreetingSuggestion {
  text: string;
  to: string;
  event?: "ai-chat:open" | "compose:focus";
}

export const GREETING_SUGGESTIONS: readonly GreetingSuggestion[] = [
  {
    text: "Meet a family member you haven't talked to in a while",
    to: "/tree",
  },
  {
    text: "Share a memory from the year you were born",
    to: "/",
    event: "compose:focus",
  },
  { text: "Add someone to the tree today", to: "/tree" },
  {
    text: "Look up where Tsegay was born",
    to: "/",
    event: "ai-chat:open",
  },
  { text: "Try a question for the family AI", to: "/", event: "ai-chat:open" },
  { text: "Browse posts from 30 years ago", to: "/" },
] as const;

const STORAGE_KEY = "greeting-suggestion";

interface CachedSuggestion {
  /** User id this suggestion was picked for. Lets us refresh on sign-out
   *  → sign-in transitions automatically. */
  userId: string;
  index: number;
}

function readCache(): CachedSuggestion | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedSuggestion;
    if (
      typeof parsed.userId !== "string" ||
      typeof parsed.index !== "number" ||
      parsed.index < 0 ||
      parsed.index >= GREETING_SUGGESTIONS.length
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(cache: CachedSuggestion): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch {
    // Quota / private mode — silently ignored.
  }
}

/**
 * Returns the suggestion to show for the given user. Stable across
 * reloads within a single sign-in session; rolls over when the user
 * changes (different account on the same browser).
 */
export function getSuggestionForUser(
  userId: string | null,
  /** Whether this user has the ai_chat beta feature. Suggestions that open
   *  the AI are dropped when they do not: the prompt is a link that fires a
   *  window event, and with no ChatBot mounted to hear it the click does
   *  nothing at all. Defaults to true so existing callers are unchanged. */
  aiEnabled = true,
): GreetingSuggestion | null {
  if (!userId) return null;

  const pool = aiEnabled
    ? GREETING_SUGGESTIONS
    : GREETING_SUGGESTIONS.filter((s) => s.event !== "ai-chat:open");
  if (pool.length === 0) return null;

  // The cache stores an index into the FULL list, so a cached AI suggestion
  // for a user who lost the feature still has to be replaced rather than
  // returned.
  const cached = readCache();
  if (cached && cached.userId === userId) {
    const hit = GREETING_SUGGESTIONS[cached.index] ?? null;
    if (hit && pool.includes(hit)) return hit;
  }

  const index = GREETING_SUGGESTIONS.indexOf(
    pool[Math.floor(Math.random() * pool.length)]!,
  );
  writeCache({ userId, index });
  return GREETING_SUGGESTIONS[index] ?? null;
}
